import { APP_CURRENCY, formatMoneyCents } from "@/lib/currency";

/**
 * Client-side affiliate referral attribution.
 *
 * A visitor who lands on /ref/:code gets the code stored locally; every
 * checkout the browser starts afterwards forwards it so the backend can tag
 * the Stripe customer with the referring athlete.
 */

const STORAGE_KEY = "coachai:ref";
/** Attribution window — 60 days, matching typical affiliate norms. */
const TTL_MS = 60 * 24 * 60 * 60 * 1000;

export const AFFILIATE_COMMISSION_RATE = 0.15;

export function normaliseRefCode(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]/g, "")
    .slice(0, 40);
}

export function storeRefCode(code: string): void {
  const clean = normaliseRefCode(code);
  if (!clean) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: clean, at: Date.now() }),
    );
  } catch {
    /* storage unavailable — attribution is best-effort */
  }
}

export function getRefCode(): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: string; at?: number };
    if (!parsed?.code) return null;
    if (typeof parsed.at === "number" && Date.now() - parsed.at > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return normaliseRefCode(parsed.code) || null;
  } catch {
    return null;
  }
}

export function clearRefCode(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

/** Canonical public site domain used for shareable links. */
export const PUBLIC_SITE_ORIGIN = "https://coachaifit.com";

/**
 * Public referral link for a code, e.g. https://coachaifit.com/ref/jane
 * Always uses the live custom domain so links shared from the Lovable
 * preview/published sandbox URLs still point at coachaifit.com.
 */
export function referralLink(code: string): string {
  return `${PUBLIC_SITE_ORIGIN}/ref/${code}`;
}


/** Suggest a URL-safe referral code from an athlete's name or handle. */
export function suggestRefCode(name: string, handle: string): string {
  const fromHandle = normaliseRefCode(handle.replace(/^@/, "").replace(/\s+/g, ""));
  if (fromHandle.length >= 3) return fromHandle;
  return normaliseRefCode(name.replace(/\s+/g, "")) || "athlete";
}

export function formatCents(cents: number, currency = APP_CURRENCY): string {
  return formatMoneyCents(cents ?? 0, currency);
}
